// src/components/auth/AcceptInvitePage.js
// Purpose: Handle magic link invite acceptance, auto-login, and redirect to appropriate product/context
 
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../config';
import { Loader, CheckCircle, XCircle, Mail } from 'lucide-react';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your invitation...');
  const [inviteData, setInviteData] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Extract parameters from URL
  const token = searchParams.get('token');
  const product = searchParams.get('product');
  const projectId = searchParams.get('projectid');
  const roundId = searchParams.get('roundid');

  const ensureFreeSubscription = useCallback(async (userId) => {
    try {
      // Check if user already has a subscription
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id, plan_type, status')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingSub) {
        // User already has a subscription, don't overwrite
        console.log('User already has subscription:', existingSub.plan_type);
        return;
      }

      // Create free subscription for viewer
      const { error: subError } = await supabase
        .from('subscriptions')
        .insert({
          user_id: userId,
          plan_type: 'free',
          status: 'active',
          amount_cents: 0,
          billing_cycle: 'monthly',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (subError) {
        console.error('Error creating free subscription:', subError);
      } else {
        console.log('Created free subscription for invited viewer');
      }
    } catch (error) {
      console.error('Error ensuring free subscription:', error);
    }
  }, []);

  const grantProductAccess = useCallback(async (userId, invite) => {
    try {
      // Ensure user has at least a free subscription
      await ensureFreeSubscription(userId);

      // Grant general product access
      if (invite.role) {
        const { error: roleError } = await supabase
          .from('user_product_roles')
          .upsert({
            user_id: userId,
            product_name: invite.inviteforproduct,
            role: invite.role
          });

        if (roleError) console.error('Error granting product role:', roleError);
      }

      // Grant specific project/round access based on product
      if (invite.inviteforproduct === 'dataroom' && invite.projectid) {
        const { error: dataroomError } = await supabase
          .from('dataroom_users')
          .upsert({
            user_id: userId,
            project_id: invite.projectid,
            role: invite.role || 'viewer',
            status: 'active',
            invited_at: new Date().toISOString()
          });

        if (dataroomError) console.error('Error granting dataroom access:', dataroomError);
      }

      if (invite.inviteforproduct === 'capraise' && invite.roundid) {
        console.log('CapRaise access granted for round:', invite.roundid);
      }

    } catch (error) {
      console.error('Error granting product access:', error);
    }
  }, [ensureFreeSubscription]);

  const markInviteAccepted = useCallback(async (inviteId, userId) => {
    try {
      const { error } = await supabase
        .from('userinvites_invites')
        .update({
          invite_status: 'accepted',
          acceptedby: userId,
          acceptedat: new Date().toISOString()
        })
        .eq('id', inviteId);

      if (error) throw error;

      // Grant user access to the product/project
      await grantProductAccess(userId, inviteData);
      
    } catch (error) {
      console.error('Error marking invite as accepted:', error);
    }
  }, [inviteData, grantProductAccess]);

  const getProductDisplayName = (productName) => {
    const displayNames = {
      'dataroom': 'Dataroom',
      'capraise': 'CapRaise',
      'equity': 'Equity',
      'questions': 'Questions'
    };
    return displayNames[productName] || productName;
  };

  // FIXED: Wrap in useCallback and use projectId, roundId variables
  const redirectToProduct = useCallback(() => {
    if (!inviteData) return;

    let redirectPath = '/';

    switch (inviteData.inviteforproduct) {
      case 'dataroom':
        redirectPath = projectId ? `/dataroom/${projectId}` : '/dataroom';
        break;
      case 'capraise':
        redirectPath = roundId ? `/capraise/rounds/${roundId}` : '/capraise';
        break;
      case 'equity':
        redirectPath = '/equity';
        break;
      case 'questions':
        redirectPath = '/questions';
        break;
      default:
        redirectPath = '/account';
    }

    navigate(redirectPath);
  }, [inviteData, projectId, roundId, navigate]);

  // FIXED: Wrap in useCallback and use product variable
  const processInvite = useCallback(async () => {
    try {
      setStatus('processing');
      setMessage('Processing your invitation...');

      const { data: invite, error: inviteError } = await supabase
        .from('userinvites_invites')
        .select('*')
        .eq('invite_token', token)
        .eq('invite_status', 'pending')
        .single();

      if (inviteError || !invite) {
        setStatus('error');
        setMessage('Invalid or expired invitation link.');
        return;
      }

      setInviteData(invite);

      // FIXED: Removed userError variable
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        if (user.email === invite.email) {
          await markInviteAccepted(invite.id, user.id);
          setStatus('success');
          setMessage(`Welcome! You've been granted access to ${getProductDisplayName(invite.inviteforproduct)} for ${product || 'this product'}.`);
        } else {
          setStatus('error');
          setMessage(`This invitation is for ${invite.email}. Please log out and try again.`);
        }
        return;
      }

      setMessage('Checking your account status...');
      
      // FIXED: Removed currentUrl variable
      const { error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        console.error('Auth error:', authError);
      }

      const { data: { user: newUser } } = await supabase.auth.getUser();
      
      if (newUser) {
        await markInviteAccepted(invite.id, newUser.id);
        setStatus('success');
        setMessage(`Welcome! You've been granted access to ${getProductDisplayName(invite.inviteforproduct)}.`);
      } else {
        setStatus('signup_required');
        setMessage(`To accept this invitation, you need to create an account with ${invite.email} or sign in.`);
      }

    } catch (error) {
      console.error('Error processing invite:', error);
      setStatus('error');
      setMessage('An error occurred while processing your invitation. Please try again.');
    }
  }, [token, product, markInviteAccepted]); // FIXED: Removed grantProductAccess from dependencies

  const handleManualSignup = () => {
    const returnUrl = window.location.href;
    navigate(`/auth?mode=signup&email=${inviteData?.email}&return=${encodeURIComponent(returnUrl)}`);
  };

  const handleManualLogin = () => {
    const returnUrl = window.location.href;
    navigate(`/auth?mode=login&email=${inviteData?.email}&return=${encodeURIComponent(returnUrl)}`);
  };

  const renderStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader className="w-16 h-16 text-blue-600 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-600" />;
      case 'error':
      case 'expired':
        return <XCircle className="w-16 h-16 text-red-600" />;
      case 'signup_required':
        return <Mail className="w-16 h-16 text-blue-600" />;
      default:
        return <Loader className="w-16 h-16 text-gray-400 animate-spin" />;
    }
  };

  // FIXED: Added processInvite to dependency array
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. No token provided.');
      return;
    }
    
    processInvite();
  }, [token, processInvite]);

  // FIXED: Added redirectToProduct to dependency array
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      redirectToProduct();
    }
  }, [status, countdown, redirectToProduct]);

  // Auth state change listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // This can be used to re-process logic if the user logs in/out on another tab
    });
    return () => subscription?.unsubscribe();
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center">
              {renderStatusIcon()}
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {status === 'processing' && 'Processing Invitation'}
              {status === 'success' && 'Invitation Accepted!'}
              {status === 'error' && 'Invitation Error'}
              {status === 'expired' && 'Invitation Expired'}
              {status === 'signup_required' && 'Account Required'}
            </h2>
          </div>

          <div className="text-center mb-6">
            <p className="text-gray-600 mb-4">{message}</p>
            
            {inviteData && (
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p><strong>Invitation for:</strong> {inviteData.email}</p>
                <p><strong>Product:</strong> {getProductDisplayName(inviteData.inviteforproduct)}</p>
                {inviteData.role && <p><strong>Role:</strong> {inviteData.role}</p>}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {status === 'success' && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Redirecting in {countdown} seconds...
                </p>
                <button
                  onClick={redirectToProduct}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Continue to {getProductDisplayName(inviteData?.inviteforproduct)}
                </button>
              </div>
            )}

            {status === 'signup_required' && (
              <div className="space-y-3">
                <button
                  onClick={handleManualSignup}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create Account with {inviteData?.email}
                </button>
                <button
                  onClick={handleManualLogin}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Sign In to Existing Account
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Having trouble? Contact the person who sent you this invitation for assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;